import { faSearch, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { OptionProps, SingleValueProps } from 'react-select-modern';
import { components } from 'react-select-modern';

import type { IGeoFilter } from '@feathr/blackbox';
import { Button, Radios, UniversalSelect, Well } from '@feathr/components';
import { cssVar } from '@feathr/hooks';

import type { IProps } from './GeoFilter.hooks';
import { useGeoFilter } from './GeoFilter.hooks';
import {
  getCacheKey,
  getGeoFilterValue,
  getIncludedValue,
  type ITypeOption,
  TYPE_OPTIONS,
} from './GeoFilter.utils';

import * as styles from './GeoFilter.css';

function renderOptionOrSingleValue(
  props: SingleValueProps<IGeoFilter> | OptionProps<IGeoFilter>,
): JSX.Element {
  const { data } = props;

  return (
    <div className={styles.option}>
      <span className={styles.label}>{data.name}</span>
      <dl className={styles.data}>
        {['Zip'].includes(data.kind) && (
          <>
            <dt className={'visuallyhidden'}>City</dt>
            <dd>{data.address.city},</dd>
          </>
        )}
        {['City', 'Zip'].includes(data.kind) && (
          <>
            <dt className={'visuallyhidden'}>Region</dt>
            <dd>{data.address.region},</dd>
          </>
        )}
        {['Region', 'City', 'Zip'].includes(data.kind) && (
          <>
            <dt className={'visuallyhidden'}>Country</dt>
            <dd>{data.address.country}</dd>
          </>
        )}
        <dt className={'visuallyhidden'}>Kind</dt>
        <dd></dd>
      </dl>
    </div>
  );
}

function Option(props: OptionProps<IGeoFilter>): JSX.Element {
  return (
    <components.Option {...props} className={styles.wrapper}>
      {renderOptionOrSingleValue(props)}
    </components.Option>
  );
}

function SingleValue(props: SingleValueProps<IGeoFilter>): JSX.Element {
  return (
    <components.SingleValue {...props} className={styles.wrapper}>
      {renderOptionOrSingleValue(props)}
    </components.SingleValue>
  );
}

export const CustomOption = Option;
export const CustomSingleValue = SingleValue;

function GeoFilter(props: Readonly<IProps>): JSX.Element {
  const { layout = 'inline' } = props;
  const { t } = useTranslation();

  const {
    geofilter,
    selectedType,
    loadOptions,
    handleIncludedChange,
    handleTypeChange,
    handleClear,
    handleRemove,
    handleSelectSingle,
  } = useGeoFilter(props);

  const cacheKey = getCacheKey(props, selectedType.id);
  const includedValue = getIncludedValue(props);

  return (
    <Well width={'full'}>
      <Flex
        className={`${styles.mainContainer} ${layout === 'inline' ? styles.inline : ''}`}
        direction={layout === 'stacked' ? 'column' : 'row'}
        gap={cssVar('--spacing-3')}
      >
        <Flex
          align={'center'}
          className={styles.topContainer}
          direction={'row'}
          gap={cssVar('--spacing-2')}
        >
          <UniversalSelect<IGeoFilter>
            async={true}
            cacheOptions={true}
            className={styles.wrapper}
            components={{ Option, SingleValue }}
            controlPrefix={<FontAwesomeIcon icon={faSearch} />}
            defaultOptions={true}
            isClearable={true}
            key={cacheKey}
            loadOptions={loadOptions}
            menuPortalTarget={document.body}
            menuPosition={'fixed'}
            name={'geo-filter'}
            onClear={handleClear}
            onSelectSingle={handleSelectSingle}
            placeholder={t('Start typing a location...')}
            value={getGeoFilterValue(geofilter)}
            wrapperClassName={styles.selectWrapper}
          />
          <UniversalSelect<ITypeOption>
            className={styles.typeSelect}
            menuPortalTarget={document.body}
            menuPosition={'fixed'}
            name={'geo-filter-type'}
            onSelectSingle={handleTypeChange}
            options={TYPE_OPTIONS}
            value={selectedType}
            wrapperClassName={styles.typeSelectWrapper}
          />
        </Flex>
        <Flex
          align={'center'}
          className={`${styles.bottomContainer} ${layout === 'inline' ? styles.inline : ''}`}
          direction={'row'}
          gap={cssVar('--spacing-2')}
          justify={layout === 'inline' ? 'flex-start' : 'space-between'}
        >
          <Radios
            className={styles.radiosWrapper}
            layout={'block'}
            onChange={handleIncludedChange}
            options={[
              { id: 'included', name: 'Included', theme: 'green' },
              { id: 'excluded', name: 'Excluded', theme: 'red' },
            ]}
            value={includedValue ? 'included' : 'excluded'}
          />
          <Button
            aria-label={t('Remove location filter')}
            name={'remove-geofilter'}
            onClick={handleRemove}
            type={'iconDanger'}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </Flex>
      </Flex>
    </Well>
  );
}

export default observer(GeoFilter);
